/*
  Ссылка в кабинет должника из карточки счёта — §3.8.2.

  До 07.09.2026 сервер умел выдавать ссылку, а нажать было негде: модуль
  кабинета должника целиком был недостижим для пользователя. Здесь и стоит та
  самая кнопка.

  Три вещи, ради которых блок устроен именно так:

  1. **Значение показывается один раз.** В базе лежит только отпечаток, и
     восстановить адрес нельзя ни нам, ни заказчику. Поэтому оно остаётся на
     экране до перезагрузки и рядом стоит кнопка «Скопировать».
  2. **Получатель выбирается из должников по счёту.** У счёта бывает несколько
     собственников; кому именно ушла ссылка - вопрос, на который потом
     отвечает аудит-след.
  3. **Выданное видно и закрывается.** Ошибся получателем - закрой ссылку, она
     перестаёт работать немедленно.

  Пока канал сообщений не подключён, ссылку отправляет человек своими руками -
  отсюда телефон рядом с фамилией и адрес в поле, из которого его копируют.
*/

import { useCallback, useState } from 'react'

import { api } from '../api/client.js'
import { DEBTOR_PATH } from '../pages/DebtorPage.jsx'
import { Confirm, Field, Loading, Notice } from './Common.jsx'
import DebtorLinkChoice from './DebtorLinkChoice.jsx'
import DebtorLinkCloseConfirm from './DebtorLinkCloseConfirm.jsx'
import DebtorLinkList from './DebtorLinkList.jsx'
import useEntryList from './entry/useEntryList.js'
import usePagedList from './usePagedList.js'

/** Адрес страницы должника: значение живёт за решёткой и на сервер не уходит. */
function addressOf(token) {
  return `${window.location.origin}${DEBTOR_PATH}#${token}`
}

export default function DebtorLink({ accountId }) {
  // Состав должников - перечень для выбора получателя, целиком; ссылки - страницы сервера
  // (21.10.2026): таблица режется там, а не в браузере.
  const loadRecipients = useCallback(() => api.debtorRecipients(accountId), [accountId])
  const { rows: recipientList, error: recipientsError } = useEntryList(loadRecipients)
  const loadLinks = useCallback((window) => api.debtorLinks(accountId, window), [accountId])
  const { rows: linkRows, error: linksError, reload, bar } = usePagedList(loadLinks)
  const [error, setError] = useState(null)
  const [hint, setHint] = useState(null)
  const [choice, setChoice] = useState({ person: '', purpose: 'operator' })
  const [issued, setIssued] = useState(null)
  const [busy, setBusy] = useState(false)
  // Отложенная выдача: что подтверждаем и что сделать, если подтвердят.
  const [pending, setPending] = useState(null)
  const [pendingError, setPendingError] = useState(null)
  // Ссылка из уведомления, которую спрашиваем, закрывать ли (23.10.2026), и отказ в том окне.
  const [closing, setClosing] = useState(null)
  const [closeError, setCloseError] = useState(null)

  /*
    🔴 У получателя действует РОВНО ОДНА ссылка (решение владельца 09.09.2026) -
    кроме ссылок из уведомлений: их по одной на каждое собранное (21.10.2026).

    Инвариант держит сервер: при выдаче он закрывает прежние ссылки этого
    получателя. Кабинет отвечает за другое - чтобы человек знал об этом ДО
    нажатия. Замена необратима: показать выданное значение второй раз
    невозможно (в базе только хеш, §3.8.2), а ссылка, уже отправленная
    должнику, после замены перестанет открывать страницу.

    Отсюда две ветки: если у получателя действующей ссылки нет, терять нечего и
    выдача идёт сразу; если есть - спрашиваем окном, называя, чья это ссылка и
    что случится.
  */
  async function askIssue() {
    /* Ссылка из уведомления (20.10.2026) живёт своим родом: ручная выдача её
       не закрывает - письмо с ней уже ушло, - и спрашивать о ней незачем.

       С 21.10.2026 в браузере лежит одна страница списка, а не все ссылки, поэтому «есть ли
       у получателя ссылка, которую заменит новая» спрашивается у сервера одной строкой
       (`limit=1`, отбор `replaceable`); ответ «нет» не ждёт подтверждения. */
    const existing = await findReplaceable()
    if (existing === undefined) return
    if (!existing) {
      await deliver(() =>
        api.issueDebtorLink(accountId, choice.purpose, choice.person || undefined),
      )
      return
    }
    setPending({
      recipient: existing.recipient ?? 'на счёт целиком',
      run: () => api.issueDebtorLink(accountId, choice.purpose, choice.person || undefined),
    })
  }

  // Ссылка выбранного получателя (или «на счёт целиком»), которую заменит новая выдача:
  // сама ссылка, `null` - такой нет, `undefined` - спросить не удалось (отказ показан).
  async function findReplaceable() {
    const only = choice.person ? { person_id: choice.person } : { whole_account: true }
    setBusy(true)
    setError(null)
    try {
      const found = await api.debtorLinks(accountId, {
        limit: 1,
        offset: 0,
        replaceable: true,
        ...only,
      })
      return found.items[0] ?? null
    } catch (failure) {
      setError(failure)
      return undefined
    } finally {
      setBusy(false)
    }
  }

  function askReissue(link) {
    setPending({
      recipient: link.recipient ?? 'на счёт целиком',
      // Получателя и способ передачи выбирает сервер по прежней ссылке: здесь
      // их нет намеренно, иначе то же решение принималось бы в двух местах.
      run: () => api.reissueDebtorLink(accountId, link.link_id),
    })
  }

  async function confirm() {
    const request = pending
    setPendingError(null)
    const failure = await deliver(request.run, {
      copyAtOnce: true,
      done: 'Прежняя ссылка закрыта - по ней страница больше не открывается.',
    })
    // Ошибка остаётся в окне: закрыть его и показать её за спиной человека
    // значит сообщить, что действие прошло, когда оно не прошло.
    if (failure) setPendingError(failure)
    else setPending(null)
  }

  /*
    Общее для выдачи и замены: показать адрес, обновить список, не молчать.

    Возвращает отказ, а не бросает его: вызывающий решает, куда его показать -
    в окне подтверждения или в самой карточке.
  */
  async function deliver(call, { copyAtOnce = false, done = '' } = {}) {
    setBusy(true)
    setError(null)
    setHint(null)
    try {
      const link = await call()
      const address = addressOf(link.token)
      setIssued({ address, expires_at: link.expires_at })
      if (copyAtOnce) await copyAddress(address, done)
      else if (done) setHint(done)
      reload()
      return null
    } catch (failure) {
      setError(failure)
      return failure
    } finally {
      setBusy(false)
    }
  }

  // Ссылку из уведомления - только через окно: она напечатана в письме, которое могло уйти.
  function askClose(link) {
    if (link.purpose === 'notice') setClosing(link)
    else close(link.link_id)
  }

  async function confirmClose() {
    setCloseError(null)
    const failure = await close(closing.link_id)
    if (failure) setCloseError(failure)
    else setClosing(null)
  }

  // Отказ возвращается, а не только показывается: окну закрытия он нужен внутри окна.
  async function close(linkId) {
    setBusy(true)
    setError(null)
    try {
      await api.revokeDebtorLink(accountId, linkId)
      setHint('Ссылка закрыта: страница по ней больше не открывается.')
      reload()
      return null
    } catch (failure) {
      setError(failure)
      return failure
    } finally {
      setBusy(false)
    }
  }

  async function copyAddress(address, done = '') {
    try {
      await navigator.clipboard.writeText(address)
      setHint(`Адрес скопирован - вставьте его в сообщение должнику. ${done}`.trim())
    } catch {
      // Браузер отказывает в буфере обмена без защищённого соединения. Молчать
      // здесь нельзя: человек решит, что скопировалось, и отправит пустоту.
      // О закрытии прежней ссылки сказать всё равно обязаны: она уже закрыта.
      setHint(`Скопировать не удалось - выделите адрес в поле и скопируйте вручную. ${done}`.trim())
    }
  }

  // Не загрузилось ничего из нужного для первого показа - одна плашка, как раньше.
  const loadError = linksError ?? recipientsError
  if (loadError && (linkRows === null || recipientList === null)) {
    return <Notice error={loadError} />
  }

  return (
    <section className="section card">
      <h2 className="section__title">Ссылка в кабинет должника</h2>

      <DebtorLinkChoice
        recipients={recipientList ?? []}
        choice={choice}
        onChoice={setChoice}
        onIssue={askIssue}
        busy={busy}
      />

      {error ? <Notice error={error} /> : null}
      {hint ? <Notice kind="ok">{hint}</Notice> : null}

      {issued ? (
        <div className="row">
          <Field label="Адрес для должника - показывается один раз" width="m">
            <input
              className="field__input"
              readOnly
              value={issued.address}
              onFocus={(event) => event.target.select()}
            />
          </Field>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => copyAddress(issued.address)}
          >
            Скопировать
          </button>
        </div>
      ) : null}

      {linkRows === null ? (
        <Loading what="Загружаем ссылки" />
      ) : (
        <DebtorLinkList
          links={linkRows}
          paging={bar}
          onClose={askClose}
          onReissue={askReissue}
          busy={busy}
        />
      )}

      {closing ? (
        <DebtorLinkCloseConfirm
          busy={busy}
          error={closeError}
          onConfirm={confirmClose}
          onCancel={() => {
            setClosing(null)
            setCloseError(null)
          }}
        />
      ) : null}

      {pending ? (
        <Confirm
          title="Выдать новую ссылку?"
          action="Выдать новую"
          busy={busy}
          error={pendingError}
          onConfirm={confirm}
          onCancel={() => {
            setPending(null)
            setPendingError(null)
          }}
        >
          У получателя «{pending.recipient}» уже есть действующая ссылка. Новая заменит её:
          прежняя перестанет открывать страницу сразу. Если вы уже отправили её должнику, он
          увидит отказ - отправьте ему новую. Новая ссылка ляжет в буфер обмена.
        </Confirm>
      ) : null}

      <div className="muted">
        Отправка ссылки сообщением появится вместе с подключённым каналом связи. Сейчас адрес
        передаёт сотрудник: в разговоре, письмом или своим сообщением.
      </div>
    </section>
  )
}
